import React, { useEffect, useRef, useState } from "react";
import AgoraRTC, { 
  IAgoraRTCClient, 
  IAgoraRTCRemoteUser, 
  ICameraVideoTrack, 
  IMicrophoneAudioTrack 
} from "agora-rtc-sdk-ng";
import { agoraConfig, CallType, TEMP_TOKEN, TEMP_CHANNEL } from "../../config/agoraConfig";
import { AgoraTokenService } from "../../services/agoraTokenService";
import { Button } from "../ui/Button";
import { PhoneOff, Users, Settings } from "lucide-react";

export interface AgoraVideoCallWithControlsProps {
  channelName: string;
  token?: string;
  uid?: number;
  callType?: CallType;
  participantName?: string;
  onCallEnd?: () => void;
  onCallStart?: () => void;
  onError?: (error: string) => void;
  autoStart?: boolean;
  className?: string;
}

export const AgoraVideoCallWithControls: React.FC<AgoraVideoCallWithControlsProps> = ({
  channelName,
  token,
  uid = 0,
  callType = CallType.VIDEO,
  participantName = "Professional",
  onCallEnd,
  onCallStart,
  onError,
  autoStart = false,
  className = ""
}) => {
  // Refs for video containers
  const localVideoRef = useRef<HTMLDivElement>(null);
  const remoteVideoContainerRef = useRef<HTMLDivElement>(null);
  
  // Agora client and tracks
  const clientRef = useRef<IAgoraRTCClient | null>(null);
  const localVideoTrackRef = useRef<ICameraVideoTrack | null>(null);
  const localAudioTrackRef = useRef<IMicrophoneAudioTrack | null>(null);
  
  // Component state
  const [isJoined, setIsJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [callStartTime, setCallStartTime] = useState<Date | null>(null);
  const [callDuration, setCallDuration] = useState<string>("00:00");

  // Initialize Agora client
  useEffect(() => {
    if (!agoraConfig.appId || agoraConfig.appId === "YOUR_AGORA_APP_ID_HERE") {
      setError("Agora App ID not configured");
      return;
    }

    // Create client
    clientRef.current = AgoraRTC.createClient({ 
      mode: "rtc", 
      codec: "vp8" 
    });

    // Set up event listeners
    const handleUserJoined = async (user: IAgoraRTCRemoteUser) => {
      setRemoteUsers(prev => [...prev, user]);
    };

    const handleUserPublished = async (user: IAgoraRTCRemoteUser, mediaType: "video" | "audio") => {
      try {
        // Subscribe to the published track
        await clientRef.current?.subscribe(user, mediaType);
        
        // Update remote users list to trigger re-render
        setRemoteUsers(prev => {
          const existing = prev.find(u => u.uid === user.uid);
          if (existing) {
            // Update existing user
            return prev.map(u => u.uid === user.uid ? user : u);
          } else {
            // Add new user
            return [...prev, user];
          }
        });
        
        if (mediaType === "video" && user.videoTrack) {
          // Remove existing video element for this user (if any)
          const existingElement = document.getElementById(`remote-video-${user.uid}`);
          if (existingElement) {
            existingElement.remove();
          }
          
          // Create individual video container for this user
          const videoElement = document.createElement("div");
          videoElement.id = `remote-video-${user.uid}`;
          videoElement.className = "w-full h-full absolute inset-0";
          
          if (remoteVideoContainerRef.current) {
            remoteVideoContainerRef.current.appendChild(videoElement);
            user.videoTrack.play(videoElement);
          }
        }
        
        if (mediaType === "audio" && user.audioTrack) {
          user.audioTrack.play();
        }
      } catch (error) {
        console.error(`❌ Failed to subscribe to ${mediaType}:`, error);
        setError(`Failed to subscribe to ${mediaType}: ${error.message}`);
      }
    };

    const handleUserUnpublished = (user: IAgoraRTCRemoteUser, mediaType: "video" | "audio") => {
      if (mediaType === "video") {
        // Remove this user's video element
        const videoElement = document.getElementById(`remote-video-${user.uid}`);
        if (videoElement) {
          videoElement.remove();
        }
      }
    };

    const handleUserLeft = (user: IAgoraRTCRemoteUser) => {
      setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
      
      // Remove this user's video element
      const videoElement = document.getElementById(`remote-video-${user.uid}`);
      if (videoElement) {
        videoElement.remove();
      }
    };

    const handleConnectionStateChange = (curState: string, prevState: string) => {
      // Connection state changes are handled silently
    };

    const handleException = (event: any) => {
      console.error("⚠️ Agora exception:", event);
      setError(`Connection error: ${event.msg || event.code || "Unknown error"}`);
    };

    // Register event listeners
    clientRef.current.on("user-joined", handleUserJoined);
    clientRef.current.on("user-published", handleUserPublished);
    clientRef.current.on("user-unpublished", handleUserUnpublished);
    clientRef.current.on("user-left", handleUserLeft);
    clientRef.current.on("connection-state-change", handleConnectionStateChange);
    clientRef.current.on("exception", handleException);

    return () => {
      // Cleanup
      if (clientRef.current) {
        clientRef.current.off("user-joined", handleUserJoined);
        clientRef.current.off("user-published", handleUserPublished);
        clientRef.current.off("user-unpublished", handleUserUnpublished);
        clientRef.current.off("user-left", handleUserLeft);
        clientRef.current.off("connection-state-change", handleConnectionStateChange);
        clientRef.current.off("exception", handleException);
      }
    };
  }, []);

  // Auto-start call if requested
  useEffect(() => {
    if (autoStart && !isJoined && !isLoading) {
      startCall();
    }
  }, [autoStart]);

  // Call duration timer
  useEffect(() => {
    if (!callStartTime) return;

    const timer = setInterval(() => {
      const now = new Date();
      const diff = now.getTime() - callStartTime.getTime();
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setCallDuration(`${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`);
    }, 1000);

    return () => clearInterval(timer);
  }, [callStartTime]);

  const startCall = async () => {
    if (!clientRef.current) {
      setError("Client not initialized");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      // Determine token and channel
      let joinToken = token;
      let finalChannelName = channelName;
      let tokenSource = "provided";

      // If no token provided, try to get one
      if (!joinToken) {
        console.log("🔑 No token provided, attempting to get token...");
        
        if (agoraConfig.useBackendTokens) {
          try {
            console.log("🌐 Requesting token from backend server...");
            const tokenResponse = await AgoraTokenService.generateToken({
              channelName: finalChannelName,
              uid: uid || 0,
              role: "publisher",
              expireTime: 3600 // 1 hour
            });
            
            joinToken = tokenResponse.token;
            finalChannelName = tokenResponse.channelName;
            tokenSource = "backend";
            
            console.log("✅ Token received from backend:", {
              channelName: finalChannelName,
              uid: tokenResponse.uid,
              expireTime: new Date(tokenResponse.expireTime * 1000).toISOString()
            });
          } catch (backendError: any) {
            console.warn("⚠️ Failed to get token from backend:", backendError.message);
            
            // Fallback to temp token for development
            if (TEMP_TOKEN && (process.env.NODE_ENV === "development" || finalChannelName === TEMP_CHANNEL)) {
              console.log("🧪 Using temporary token as fallback");
              joinToken = TEMP_TOKEN;
              finalChannelName = TEMP_CHANNEL;
              tokenSource = "temp";
            } else {
              throw new Error(`Backend token failed: ${backendError.message}`);
            }
          }
        } else {
          // Use temp token for testing if available
          if (TEMP_TOKEN && (finalChannelName === TEMP_CHANNEL || process.env.NODE_ENV === "development")) {
            console.log("🧪 Using temporary token for testing");
            joinToken = TEMP_TOKEN;
            finalChannelName = TEMP_CHANNEL;
            tokenSource = "temp";
          }
        }
      }

      // Create and publish local tracks
      const tracks = [];
      
      // Create audio track
      const audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        encoderConfig: "music_standard"
      });
      localAudioTrackRef.current = audioTrack;
      tracks.push(audioTrack);

      // Create video track if video call
      if (callType === CallType.VIDEO) {
        const videoTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: "480p_1"
        });
        localVideoTrackRef.current = videoTrack;
        tracks.push(videoTrack);
        
        // Play local video
        if (localVideoRef.current) {
          videoTrack.play(localVideoRef.current);
        }
      }

      // Join channel
      await clientRef.current.join(agoraConfig.appId, finalChannelName, joinToken || null, uid);
      
      // Publish tracks
      await clientRef.current.publish(tracks);
      
      setIsJoined(true);
      setCallStartTime(new Date());
      onCallStart?.();
      
    } catch (error: any) {
      console.error("❌ Failed to start call:", error);
      setError(`Failed to start call: ${error.message}`);
      onError?.(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const endCall = async () => {
    try {
      // Stop and close local tracks
      if (localAudioTrackRef.current) {
        localAudioTrackRef.current.stop();
        localAudioTrackRef.current.close();
        localAudioTrackRef.current = null;
      }
      
      if (localVideoTrackRef.current) {
        localVideoTrackRef.current.stop();
        localVideoTrackRef.current.close();
        localVideoTrackRef.current = null;
      }

      // Leave channel
      if (clientRef.current && isJoined) {
        await clientRef.current.leave();
      }

      // Clear all remote video elements
      if (remoteVideoContainerRef.current) {
        remoteVideoContainerRef.current.innerHTML = "";
      }

      // Reset state
      setIsJoined(false);
      setCallStartTime(null);
      setCallDuration("00:00");
      setRemoteUsers([]);
      setError("");

      onCallEnd?.();
      
    } catch (error: any) {
      console.error("❌ Error ending call:", error);
      setError(`Error ending call: ${error.message}`);
    }
  };

  // Agora's built-in mute/unmute functions
  const toggleMute = async () => {
    if (localAudioTrackRef.current) {
      const isEnabled = localAudioTrackRef.current.enabled;
      await localAudioTrackRef.current.setEnabled(!isEnabled);
    }
  };

  const toggleVideo = async () => {
    if (localVideoTrackRef.current) {
      const isEnabled = localVideoTrackRef.current.enabled;
      await localVideoTrackRef.current.setEnabled(!isEnabled);
    }
  };

  return (
    <div className={`flex flex-col h-full bg-gray-900 text-white relative ${className}`}>
      {/* Error Display */}
      {error && (
        <div className="absolute top-4 left-4 right-4 z-50 bg-red-500 text-white p-3 rounded-lg">
          <p className="text-sm">{error}</p>
          <button 
            onClick={() => setError("")}
            className="absolute top-2 right-2 text-white hover:text-gray-200"
          >
            ×
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
            <p>Connecting...</p>
          </div>
        </div>
      )}

      {/* Video Layout */}
      <div className="flex-1 relative">
        {/* Remote Video (Main) */}
        <div className="w-full h-full bg-gray-800 relative">
          {/* Remote video container */}
          <div 
            ref={remoteVideoContainerRef}
            className="w-full h-full relative"
          />
          
          {/* Waiting for participants */}
          {remoteUsers.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-center z-10">
              <div>
                <div className="w-20 h-20 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-400">Waiting for {participantName} to join...</p>
                {isJoined && (
                  <p className="text-gray-500 text-sm mt-2">You are connected. Waiting for others...</p>
                )}
              </div>
            </div>
          )}
          
          {/* No video tracks available */}
          {remoteUsers.length > 0 && !remoteUsers.some(u => u.videoTrack) && (
            <div className="absolute inset-0 flex items-center justify-center text-center z-10">
              <div>
                <div className="w-20 h-20 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-10 h-10 text-gray-400" />
                </div>
                <p className="text-gray-400">
                  {remoteUsers.length === 1 
                    ? `${participantName} has video disabled`
                    : `${remoteUsers.length} participants have video disabled`
                  }
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Audio only: {remoteUsers.filter(u => u.audioTrack).length} participants
                </p>
              </div>
            </div>
          )}
          
          {/* Participant count indicator */}
          {remoteUsers.length > 0 && (
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 rounded-lg px-3 py-1 z-20">
              <div className="flex items-center gap-2 text-sm text-white">
                <Users className="w-4 h-4" />
                <span>{remoteUsers.length + 1} participants</span>
              </div>
            </div>
          )}
        </div>

        {/* Local Video (Picture-in-Picture) */}
        {callType === CallType.VIDEO && (
          <div className="absolute top-4 right-4 w-48 h-36 bg-gray-700 rounded-lg overflow-hidden border-2 border-gray-600">
            <div 
              ref={localVideoRef}
              className="w-full h-full"
            />
          </div>
        )}

        {/* Call Info */}
        <div className="absolute top-4 left-4 bg-black bg-opacity-50 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span>{callDuration}</span>
          </div>
          <p className="text-xs text-gray-300 mt-1">
            {remoteUsers.length > 0 ? `Connected with ${participantName}` : `Calling ${participantName}...`}
          </p>
        </div>


      </div>

      {/* Controls */}
      <div className="p-6 bg-gray-800">
        <div className="flex items-center justify-center gap-4">
          {/* Start Call Button (if not joined) */}
          {!isJoined && !isLoading && (
            <Button
              onClick={startCall}
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-full"
            >
              Start Call
            </Button>
          )}

          {/* Call Controls (when joined) */}
          {isJoined && (
            <>
              {/* Mute/Unmute - Uses Agora's built-in track control */}
              <Button
                onClick={toggleMute}
                variant="ghost"
                className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600"
                title={localAudioTrackRef.current?.enabled ? "Mute" : "Unmute"}
              >
                <div className="w-6 h-6 bg-white rounded" style={{
                  WebkitMask: localAudioTrackRef.current?.enabled ? 
                    "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"><path d=\"M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z\"/><path d=\"M19 10v2a7 7 0 0 1-14 0v-2\"/><line x1=\"12\" y1=\"19\" x2=\"12\" y2=\"23\"/><line x1=\"8\" y1=\"23\" x2=\"16\" y2=\"23\"/></svg>') center/contain no-repeat'" :
                    "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"><line x1=\"1\" y1=\"1\" x2=\"23\" y2=\"23\"/><path d=\"M9 9v3a3 3 0 0 0 5.12 2.12l1.77-1.77\"/><path d=\"M17 16.95A7 7 0 0 1 5 12v-2m14-.5v2.5\"/><path d=\"M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z\"/><line x1=\"12\" y1=\"19\" x2=\"12\" y2=\"23\"/><line x1=\"8\" y1=\"23\" x2=\"16\" y2=\"23\"/></svg>') center/contain no-repeat",
                  mask: localAudioTrackRef.current?.enabled ? 
                    "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"><path d=\"M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z\"/><path d=\"M19 10v2a7 7 0 0 1-14 0v-2\"/><line x1=\"12\" y1=\"19\" x2=\"12\" y2=\"23\"/><line x1=\"8\" y1=\"23\" x2=\"16\" y2=\"23\"/></svg>') center/contain no-repeat" :
                    "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"><line x1=\"1\" y1=\"1\" x2=\"23\" y2=\"23\"/><path d=\"M9 9v3a3 3 0 0 0 5.12 2.12l1.77-1.77\"/><path d=\"M17 16.95A7 7 0 0 1 5 12v-2m14-.5v2.5\"/><path d=\"M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z\"/><line x1=\"12\" y1=\"19\" x2=\"12\" y2=\"23\"/><line x1=\"8\" y1=\"23\" x2=\"16\" y2=\"23\"/></svg>') center/contain no-repeat"
                }} />
              </Button>

              {/* Video On/Off - Uses Agora's built-in track control */}
              {callType === CallType.VIDEO && (
                <Button
                  onClick={toggleVideo}
                  variant="ghost"
                  className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600"
                  title={localVideoTrackRef.current?.enabled ? "Turn off video" : "Turn on video"}
                >
                  <div className="w-6 h-6 bg-white rounded" style={{
                    WebkitMask: localVideoTrackRef.current?.enabled ? 
                      "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"><polygon points=\"23 7 16 12 23 17 23 7\"/><rect x=\"1\" y=\"5\" width=\"15\" height=\"14\" rx=\"2\" ry=\"2\"/></svg>') center/contain no-repeat" :
                      "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"><path d=\"M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1-.34L23 7v10\"/><line x1=\"1\" y1=\"1\" x2=\"23\" y2=\"23\"/></svg>') center/contain no-repeat",
                    mask: localVideoTrackRef.current?.enabled ? 
                      "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"><polygon points=\"23 7 16 12 23 17 23 7\"/><rect x=\"1\" y=\"5\" width=\"15\" height=\"14\" rx=\"2\" ry=\"2\"/></svg>') center/contain no-repeat" :
                      "url('data:image/svg+xml,<svg xmlns=\"http://www.w3.svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\"><path d=\"M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1-.34L23 7v10\"/><line x1=\"1\" y1=\"1\" x2=\"23\" y2=\"23\"/></svg>') center/contain no-repeat"
                  }} />
                </Button>
              )}

              {/* Settings */}
              <Button
                variant="ghost"
                className="w-12 h-12 rounded-full bg-gray-700 hover:bg-gray-600"
                title="Settings"
              >
                <Settings className="w-6 h-6" />
              </Button>

              {/* Leave Call */}
              <div className="flex flex-col items-center">
                <Button
                  onClick={endCall}
                  className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600"
                  title="Leave Meeting"
                >
                  <PhoneOff className="w-6 h-6" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
